<template>
  <div class="pcc-shell" :class="{ 'pcc-shell-embedded': embedded }">
    <!-- Sidebar: conversations + participants, same as the web app. On a phone
         it slides in over the stream (there is no room for two columns), driven
         by the hamburger in the thread header. -->
    <aside class="pcc-side" :class="{ 'pcc-side-open': sideOpen }">
      <div class="pcc-side-head">
        <span class="pcc-side-label">{{ $t('message.projects.chatConversations') }}</span>
        <button type="button" class="pcc-new" @click="openNewThread">
          {{ $t('message.projects.chatNewPrivate') }}
        </button>
      </div>

      <div class="pcc-threadlist">
        <div v-for="th in threads" :key="th.id" class="pcc-thread"
          :class="{ 'pcc-thread-active': th.id === activeThread }" :title="th.name"
          @click="openThread(th.id)">
          <span v-if="!th.is_private" class="pcc-thread-ic">#</span>
          <span v-else class="pcc-facepile">
            <v-avatar v-for="p in otherMembers(th).slice(0, 3)" :key="p.id" size="18"
              :color="p.color || 'grey'" class="pcc-face">
              <span class="white--text pcc-initials-xs">{{ p.initials }}</span>
            </v-avatar>
          </span>

          <span class="pcc-thread-txt">
            <span class="pcc-thread-name">{{ threadLabel(th) }}</span>
            <span class="pcc-thread-last">
              <template v-if="th.last">{{ firstName(th.last.user) }}: {{ th.last.body }}</template>
              <em v-else class="pcc-thread-none">&mdash;</em>
            </span>
          </span>

          <span v-if="th.unread" class="pcc-badge">{{ th.unread }}</span>
        </div>
      </div>

      <div class="pcc-side-section">
        <div class="pcc-side-head">
          <span class="pcc-side-label">{{ $t('message.projects.chatParticipants') }}</span>
          <span class="pcc-count">{{ participants.length }}</span>
        </div>
        <div class="pcc-members">
          <div v-for="p in participants" :key="p.id" class="pcc-member"
            :class="{ 'pcc-member-me': isMe(p) }" :title="p.name">
            <v-avatar size="22" :color="p.color || 'grey'">
              <span class="white--text pcc-initials-sm">{{ p.initials }}</span>
            </v-avatar>
            <span class="pcc-member-name">{{ p.name }}</span>
            <span v-if="isMe(p)" class="pcc-you">{{ $t('message.projects.chatYou') }}</span>
          </div>
        </div>
      </div>
    </aside>

    <!-- Scrim so tapping outside closes the drawer on small screens -->
    <div v-if="sideOpen" class="pcc-scrim" @click="sideOpen = false"></div>

    <!-- Main column -->
    <section class="pcc-main">
      <div class="pcc-thread-head">
        <button type="button" class="pcc-hamb" @click="sideOpen = !sideOpen">
          <v-icon small>mdi-menu</v-icon>
        </button>
        <div class="pcc-thread-head-txt">
          <div class="pcc-headtitle">
            {{ activeThreadObj ? threadLabel(activeThreadObj) : '' }}
            <span v-if="activeThreadObj && activeThreadObj.is_private" class="pcc-lockpill">
              {{ $t('message.projects.chatPrivate') }}
            </span>
            <!-- 4918 follow-up: edit who is in this private conversation. Shown
                 only when the server says this user may (creator / project
                 leader / admin); the endpoint re-checks it. -->
            <v-btn v-if="canManageActive" icon x-small class="ml-1"
              :title="$t('message.projects.chatEditMembers')" @click="openEditMembers">
              <v-icon x-small>mdi-account-plus</v-icon>
            </v-btn>
          </div>
          <div class="pcc-headmeta">{{ headMeta }}</div>
        </div>
      </div>

      <!-- Message stream -->
      <div class="pcc-stream" ref="stream">
      <div v-if="loading" class="pcc-center">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>

      <div v-else-if="!messages.length" class="pcc-center pcc-empty">
        {{ $t('message.projects.chatEmpty') }}
      </div>

      <template v-else>
        <template v-for="(msg, index) in messages">
          <!-- Day separator -->
          <div class="pcc-day" v-if="showDay(index)" :key="`d-${msg.id}`">
            <span>{{ dayLabel(msg.day) }}</span>
          </div>

          <!-- Unread separator, same as the web app -->
          <div class="pcc-unread" v-if="msg.id === firstUnreadId" :key="`u-${msg.id}`">
            <span>{{ unreadCount }}</span>
          </div>

          <div :key="msg.id" class="pcc-row"
            :class="[{ 'pcc-me': msg.mine, 'pcc-grouped': isGrouped(index) }, msg.kind ? 'pcc-sys pcc-sys-' + msg.kind : '']">
            <!-- Avatar column is always reserved so grouped rows stay aligned -->
            <div class="pcc-av-slot">
              <v-avatar v-if="!isGrouped(index)" :color="(msg.user || {}).color || 'grey'" size="26">
                <span class="white--text pcc-initials">{{ (msg.user || {}).initials }}</span>
              </v-avatar>
            </div>

            <div class="pcc-body">
              <!-- Header only on the first message of a turn; grouped rows
                   inherit it and reveal their time on tap/hover instead. -->
              <div class="pcc-head" v-if="!isGrouped(index)">
                <span class="pcc-name">{{ (msg.user || {}).name }}</span>
                <span v-if="msg.mine" class="pcc-you-tag">{{ $t('message.projects.chatYou') }}</span>
                <span class="pcc-time">{{ msg.time }}</span>
              </div>

              <!-- Inline edit -->
              <template v-if="editingId === msg.id">
                <div class="pcc-edit-box">
                  <v-textarea outlined dense hide-details rows="2" auto-grow v-model="editBody"
                    class="pcc-edit"></v-textarea>
                  <div class="pcc-edit-actions">
                    <v-btn x-small text @click="cancelEdit">{{ $t('message.common.cancel') }}</v-btn>
                    <v-btn x-small color="primary" :loading="saving" @click="saveEdit(msg)">
                      {{ $t('message.common.save') }}
                    </v-btn>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="pcc-bubble-wrap">
                  <div class="pcc-text">
                    <v-icon v-if="msg.kind" size="15" class="pcc-sys-ic">{{ msg.kind === 'field_approved' ? 'mdi-check-circle' : (msg.kind === 'field_rejected' ? 'mdi-close-circle' : 'mdi-hammer-wrench') }}</v-icon>{{ msg.body }}
                    <!-- System messages about field welds open the weld map. -->
                    <button v-if="msg.kind && msg.drawing_id" type="button" class="pcc-sys-link" @click.stop="openWeldMapping(msg)">
                      {{ $t('message.projects.chatOpenWeldMapping') }} <v-icon size="14" class="ic-inherit">mdi-arrow-right</v-icon>
                    </button>
                  </div>
                  <span v-if="isGrouped(index)" class="pcc-time pcc-time-inline">{{ msg.time }}</span>
                  <span v-if="msg.can_edit || msg.can_delete" class="pcc-acts">
                    <button v-if="msg.can_edit" type="button" class="pcc-act" @click="startEdit(msg)">
                      <v-icon x-small>mdi-pencil</v-icon>
                    </button>
                    <button v-if="msg.can_delete" type="button" class="pcc-act pcc-act-danger"
                      @click="confirmDelete(msg)">
                      <v-icon x-small>mdi-delete</v-icon>
                    </button>
                  </span>
                </div>

                <div class="pcc-foot" v-if="msg.edited || msg.mine">
                  <span v-if="msg.edited" class="pcc-edited">({{ $t('message.projects.chatEdited') }})</span>
                  <span v-if="msg.mine" class="pcc-sent">
                    <v-icon x-small :color="msg.seen_count ? 'primary' : ''">
                      {{ msg.seen_count ? 'mdi-check-all' : 'mdi-check' }}
                    </v-icon>
                    <!-- Words, not a bare number, matching the web app's
                         "Seen by 1" / "Sent". -->
                    {{ msg.seen_count
                        ? $t('message.projects.chatSeenBy', { count: msg.seen_count })
                        : $t('message.projects.chatSent') }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </template>
      </template>
      </div>

      <!-- Composer -->
      <div class="pcc-compose">
        <v-textarea outlined dense hide-details rows="1" auto-grow class="pcc-input"
          :placeholder="$t('message.projects.chatPlaceholder')" v-model="draft"
          @keydown.enter.exact.prevent="send"></v-textarea>
        <v-btn class="pcc-send" color="primary" depressed :loading="sending"
          :disabled="!draft.trim()" @click="send">
          {{ $t('message.projects.chatSend') }}
        </v-btn>
      </div>
    </section>

    <!-- New private conversation / edit its members (same picker) -->
    <v-dialog v-model="newThreadDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1">
          {{ memberPickerMode === 'edit'
              ? $t('message.projects.chatEditMembers')
              : $t('message.projects.chatNewPrivateTitle') }}
        </v-card-title>
        <v-card-text class="pb-0">
          <div class="pcc-hint mb-2">
            {{ memberPickerMode === 'edit'
                ? $t('message.projects.chatEditMembersHint')
                : $t('message.projects.chatNewPrivateHint') }}
          </div>
          <div v-for="p in selectableMembers" :key="p.id" class="pcc-pick">
            <v-checkbox dense hide-details class="mt-0 pt-0" v-model="newThreadMembers"
              :value="p.id" :label="p.name"></v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="newThreadDialog = false">{{ $t('message.common.cancel') }}</v-btn>
          <v-btn color="primary" small :loading="creating"
            :disabled="memberPickerMode === 'create' && !newThreadMembers.length"
            @click="submitMemberPicker">
            {{ memberPickerMode === 'edit'
                ? $t('message.common.save')
                : $t('message.projects.chatCreate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="320">
      <v-card>
        <v-card-title class="subtitle-1">{{ $t('message.projects.chatConfirmDelete') }}</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="deleteDialog = false">{{ $t('message.common.cancel') }}</v-btn>
          <v-btn color="error" small :loading="deleting" @click="doDelete">
            {{ $t('message.common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * 4975 — internal project chat (PWA).
 *
 * Talks to the same endpoints and the same websocket channels as the web app,
 * so a message sent here appears there instantly and vice versa.
 *
 * Delivery is websocket-first with a polled fallback: 2s while no socket is up,
 * 20s once one is, re-picked the moment the transport changes. Polling is never
 * switched off — a dead socket must make chat slower, never silent.
 */
import { hostAppApi } from '../../plugins/axios_settings'
import { mapGetters } from 'vuex'
import moment from 'moment'

const FAST_MS = 2000
const SLOW_MS = 20000

export default {
  props: {
    /** Project id when embedded; omitted when used as a routed page. */
    project: { type: [String, Number], default: null },
    /** Trims the chrome that only makes sense on a full page. */
    embedded: { type: Boolean, default: false }
  },

  data () {
    return {
      // Falls back to the route so this component still works as a full page
      // (/projects/:id/chat); the prop is what lets it be embedded in a dialog.
      projectId: this.project || this.$route.params.project_id,
      threads: [],
      messages: [],
      // Read watermark, needed to place the unread separator. Captured on load
      // and NOT advanced by markRead(), so the separator stays put while the
      // user reads instead of jumping away as soon as it is acknowledged.
      watermark: 0,
      activeThread: null,
      me: null,
      draft: '',
      loading: true,
      sending: false,
      saving: false,
      deleting: false,
      editingId: null,
      editBody: '',
      deleteDialog: false,
      pendingDelete: null,
      timer: null,
      currentMs: null,
      // thread id -> off() returned by $projectChatRt.joinThread(), so this
      // component detaches exactly its own realtime handlers on destroy.
      rtOff: {},
      // Unsubscribe handle for the realtime transport-change listener.
      rtOffTransport: null,
      // Sidebar is a slide-over on phones; on wide screens CSS pins it open and
      // this flag is irrelevant.
      sideOpen: false,
      newThreadDialog: false,
      newThreadMembers: [],
      creating: false,
      // The member picker serves both creating a private conversation and
      // editing an existing one's membership.
      memberPickerMode: 'create'
    }
  },

  computed: {
    ...mapGetters(['getHostRefApi']),
    base () {
      return `${this.getHostRefApi}project/${this.projectId}/chat`
    },

    /** Messages that arrived since this user's read watermark. */
    unreadMessages () {
      return this.messages.filter(m => m.id > this.watermark && !m.mine)
    },

    /** Where to draw the "new messages" separator, as the web app does. */
    firstUnreadId () {
      return this.unreadMessages.length ? this.unreadMessages[0].id : null
    },

    unreadCount () {
      return this.unreadMessages.length
    },

    activeThreadObj () {
      return this.threads.find(t => t.id === this.activeThread) || null
    },

    /**
     * May this user change who is in the OPEN conversation? Server-decided
     * (creator / project leader / admin) and re-checked by the endpoint — this
     * only decides whether the button renders.
     */
    canManageActive () {
      const th = this.activeThreadObj
      return !!(th && th.is_private && th.can_manage)
    },

    /**
     * Participants of the open thread, current user first then alphabetical —
     * easier to scan than the raw id order the API returns. Same ordering the
     * web app applies.
     */
    participants () {
      const people = (this.activeThreadObj || {}).members || []

      return people.slice().sort((a, b) => {
        if (this.isMe(a)) return -1
        if (this.isMe(b)) return 1
        return (a.name || '').localeCompare(b.name || '')
      })
    },

    /** Everyone except me — the candidates for a new private conversation. */
    selectableMembers () {
      return this.participants.filter(p => !this.isMe(p))
    },

    /** Sub-heading under the thread name, mirroring the web app's wording. */
    headMeta () {
      const th = this.activeThreadObj
      if (!th) return ''

      if (th.is_private) {
        return this.$t('message.projects.chatPrivateOnly', {
          names: (th.members || []).map(m => m.name).join(', ')
        })
      }

      return this.$t('message.projects.chatEveryone', { count: (th.members || []).length })
    }
  },

  watch: {
    // Tell the realtime layer which conversation is on screen, so the app-wide
    // notifier does not toast messages the user is already reading here.
    activeThread: {
      immediate: true,
      handler (id) {
        if (this.$projectChatRt) this.$projectChatRt.setViewing(id)
      }
    }
  },

  mounted () {
    this.askNotificationPermission()

    // Follow the transport the moment it changes rather than on the next tick:
    // in slow mode that tick is up to 20 s away, which is how long a dead
    // socket would otherwise go unnoticed here. Registered before the first
    // load so a socket that comes up during it is seen too.
    if (this.$projectChatRt) {
      this.rtOffTransport = this.$projectChatRt.onTransportChange(this.onTransportChange)
    }
    document.addEventListener('visibilitychange', this.onVisibility)

    this.loadThreads()
      .then(() => this.loadMessages(true))
      .then(() => {
        this.subscribe()
        this.schedule()
      })
      .catch(() => {
        this.loading = false
        this.$root.$emit('snackbar', {
          snackbar: true, color: 'error', text: 'message.common.somethingWentWrong'
        })
      })
  },

  beforeDestroy () {
    if (this.timer) clearInterval(this.timer)
    document.removeEventListener('visibilitychange', this.onVisibility)
    if (this.rtOffTransport) {
      this.rtOffTransport()
      this.rtOffTransport = null
    }

    // Detach only THIS component's handlers.
    //
    // The channels themselves stay joined on purpose: they are shared with
    // App.vue's notifier, which needs them to keep raising new-message
    // notifications everywhere else in the app, and joinThread() reuses them
    // when the chat is opened again. What must not survive is a destroyed
    // component's methods still receiving events — that is what off() removes,
    // and it is what lets the notifier toast a thread the user had open a
    // moment ago instead of the events going to a dead component.
    Object.keys(this.rtOff).forEach(id => {
      try { this.rtOff[id]() } catch (e) { /* ignore */ }
    })
    this.rtOff = {}
    if (this.$projectChatRt) this.$projectChatRt.setViewing(null)
  },

  methods: {
    /**
     * Ask for OS-notification permission, once, on opening a chat.
     *
     * This is the right moment to ask: the user has just shown they care about
     * this conversation, so the prompt has obvious context. Asking on app
     * launch instead gets reflexively denied, and a denial is permanent.
     *
     * Purely an enhancement — the in-app snackbar fires either way, so a denial
     * costs nothing. App.vue's notifier is what actually raises them.
     */
    askNotificationPermission () {
      try {
        if (!window.Notification) return
        if (Notification.permission !== 'default') return
        Notification.requestPermission().catch(() => {})
      } catch (e) { /* unsupported — snackbars still work */ }
    },

    /* ---------------------------------------------------------------- */
    /* Loading                                                           */
    /* ---------------------------------------------------------------- */

    loadThreads () {
      return hostAppApi.get(`${this.base}/threads`).then(response => {
        const data = response.data || {}
        this.threads = data.threads || []
        this.me = data.me

        // A deep link (or a stale selection) may name a thread this user cannot
        // see; fall back rather than showing a pane that will never load.
        if (this.activeThread && !this.threads.some(t => t.id === this.activeThread)) {
          this.activeThread = null
        }
        if (!this.activeThread && this.threads.length) {
          this.activeThread = this.threads[0].id
        }
      })
    },

    loadMessages (markRead) {
      if (!this.activeThread) {
        this.loading = false
        return Promise.resolve()
      }

      return hostAppApi.get(`${this.base}/threads/${this.activeThread}/messages`)
        .then(response => {
          const data = response.data || {}
          this.messages = data.messages || []
          // Only take the watermark on the initial load; a refresh triggered by
          // a read receipt must not move the separator out from under the user.
          if (markRead) this.watermark = data.watermark || 0
          this.loading = false
          this.scrollToBottom()
          if (markRead && this.messages.length) this.markRead()
        })
    },

    openThread (id) {
      if (id === this.activeThread) {
        this.sideOpen = false
        return
      }
      this.activeThread = id
      this.sideOpen = false
      this.loading = true
      this.messages = []
      this.loadMessages(true)
    },

    /* ---------------------------------------------------------------- */
    /* Sidebar helpers                                                   */
    /* ---------------------------------------------------------------- */

    isMe (person) {
      return !!(this.me && person && person.id === this.me.id)
    },

    otherMembers (thread) {
      return ((thread || {}).members || []).filter(p => !this.isMe(p))
    },

    openWeldMapping (msg) {
      this.$router.push({
        name: 'weld-mapping-drawing',
        params: { project_id: this.$route.params.project_id, drawing_id: msg.drawing_id }
      })
    },
    firstName (name) {
      return String(name || '').split(' ')[0]
    },

    /**
     * A private thread is named after its members, which truncates to an
     * unreadable stub in a narrow column — so show first names (and a +N
     * overflow), exactly as the web app does. The shared thread keeps its name.
     */
    threadLabel (thread) {
      if (!thread) return ''
      if (!thread.is_private) return thread.name
      // Field-weld review groups keep their own name (welder · drawing).
      if (thread.kind === 'field_review') return thread.name

      const others = this.otherMembers(thread)
      if (!others.length) return thread.name

      let label = others.slice(0, 3).map(p => this.firstName(p.name)).join(', ')
      if (others.length > 3) label += ` +${others.length - 3}`

      return label
    },

    /* ---------------------------------------------------------------- */
    /* New private conversation                                          */
    /* ---------------------------------------------------------------- */

    openNewThread () {
      this.memberPickerMode = 'create'
      this.newThreadMembers = []
      this.newThreadDialog = true
      this.sideOpen = false
    },

    /** Edit the open private conversation's membership. */
    openEditMembers () {
      const th = this.activeThreadObj
      if (!th || !th.is_private || !th.can_manage) return

      this.memberPickerMode = 'edit'
      // Preselect the current members. You are excluded from the list (see
      // selectableMembers) and re-added on save, so you cannot accidentally
      // remove yourself from your own conversation here.
      this.newThreadMembers = (th.members || [])
        .filter(p => !this.isMe(p))
        .map(p => p.id)
      this.newThreadDialog = true
      this.sideOpen = false
    },

    submitMemberPicker () {
      if (this.memberPickerMode === 'edit') this.saveMembers()
      else this.createThread()
    },

    saveMembers () {
      const th = this.activeThreadObj
      if (!th) return

      const members = this.newThreadMembers.slice()
      // Keep yourself in: the picker never lists you.
      if (this.me && !members.includes(this.me.id)) members.push(this.me.id)

      this.creating = true
      hostAppApi.post(`${this.base}/threads/${th.id}/members`, { members })
        .then(response => {
          const data = response.data || {}
          this.newThreadDialog = false

          // Dropped below two people — the server soft-deleted it, so fall back
          // to the project-wide thread instead of showing a dead conversation.
          if (data.deleted) {
            this.activeThread = null
            this.messages = []
          }

          return this.loadThreads().then(() => {
            this.subscribe()
            return this.loadMessages(false)
          })
        })
        .catch(() => {
          this.$root.$emit('snackbar', {
            snackbar: true, color: 'error', text: 'message.common.somethingWentWrong'
          })
        })
        .finally(() => { this.creating = false })
    },

    createThread () {
      if (!this.newThreadMembers.length) return

      this.creating = true
      hostAppApi.post(`${this.base}/threads`, { members: this.newThreadMembers })
        .then(response => {
          const thread = (response.data || {}).thread
          this.newThreadDialog = false
          // Reload the list so the new thread arrives with its members and
          // preview filled in, then open it.
          return this.loadThreads().then(() => {
            if (thread) this.openThread(thread.id)
            this.subscribe()
          })
        })
        .catch(() => {
          this.$root.$emit('snackbar', {
            snackbar: true, color: 'error', text: 'message.common.somethingWentWrong'
          })
        })
        .finally(() => { this.creating = false })
    },

    markRead () {
      if (!this.activeThread || !this.messages.length) return
      const lastId = this.messages[this.messages.length - 1].id

      hostAppApi.post(`${this.base}/threads/${this.activeThread}/read`, { last_message_id: lastId })
        .then(() => {
          const th = this.threads.find(t => t.id === this.activeThread)
          if (th) th.unread = 0
        })
        .catch(() => { /* non-fatal */ })
    },

    /* ---------------------------------------------------------------- */
    /* Sending / editing / deleting                                      */
    /* ---------------------------------------------------------------- */

    send () {
      const body = this.draft.trim()
      if (!body || this.sending) return

      this.sending = true
      hostAppApi.post(`${this.base}/threads/${this.activeThread}/messages`, { body })
        .then(response => {
          const msg = (response.data || {}).message
          if (msg) {
            this.messages.push(msg)
            this.scrollToBottom()
          }
          this.draft = ''
        })
        .catch(() => {
          this.$root.$emit('snackbar', {
            snackbar: true, color: 'error', text: 'message.common.somethingWentWrong'
          })
        })
        .finally(() => { this.sending = false })
    },

    startEdit (msg) {
      this.editingId = msg.id
      this.editBody = msg.body
    },

    cancelEdit () {
      this.editingId = null
      this.editBody = ''
    },

    saveEdit (msg) {
      const body = this.editBody.trim()
      if (!body) return

      this.saving = true
      hostAppApi.post(`${this.base}/threads/${this.activeThread}/messages/${msg.id}/update`, { body })
        .then(() => {
          msg.body = body
          msg.edited = true
          this.cancelEdit()
        })
        .catch(() => {
          this.$root.$emit('snackbar', {
            snackbar: true, color: 'error', text: 'message.common.somethingWentWrong'
          })
        })
        .finally(() => { this.saving = false })
    },

    confirmDelete (msg) {
      this.pendingDelete = msg
      this.deleteDialog = true
    },

    doDelete () {
      const msg = this.pendingDelete
      if (!msg) return

      this.deleting = true
      hostAppApi.post(`${this.base}/threads/${this.activeThread}/messages/${msg.id}/delete`)
        .then(() => {
          this.messages = this.messages.filter(m => m.id !== msg.id)
          this.deleteDialog = false
          this.pendingDelete = null
        })
        .catch(() => {
          this.$root.$emit('snackbar', {
            snackbar: true, color: 'error', text: 'message.common.somethingWentWrong'
          })
        })
        .finally(() => { this.deleting = false })
    },

    /* ---------------------------------------------------------------- */
    /* Realtime                                                          */
    /* ---------------------------------------------------------------- */

    subscribe () {
      if (!this.$projectChatRt) return

      // Subscribe to every visible thread, not just the open one: a message in
      // another conversation still has to move its unread badge. Register once
      // per thread: joinThread() APPENDS handlers, so registering the same
      // thread again would deliver every event to this component twice.
      this.threads.forEach(th => {
        if (this.rtOff[th.id]) return
        this.rtOff[th.id] = this.$projectChatRt.joinThread(th.id, {
          created: this.onRemoteCreated,
          updated: this.onRemoteUpdated,
          deleted: this.onRemoteDeleted,
          read: this.onRemoteRead
        })
      })

      this.schedule()
    },

    onRemoteCreated (payload) {
      const msg = payload.message || {}

      if (payload.thread_id === this.activeThread) {
        // De-dupe on id: our own POST response lands first, and a reconnect can
        // replay events.
        if (!this.messages.some(m => m.id === msg.id)) {
          // Sample the scroll position BEFORE appending: scrollToBottom() runs
          // on $nextTick and would move us to the bottom, making every message
          // look read.
          const wasAtBottom = this.isViewingLatest()

          this.messages.push(msg)

          // Only follow along and acknowledge if the reader was already at the
          // newest message. Scrolled up in history, or app in the background?
          // Leave it unread and let the unread divider do its job.
          if (wasAtBottom) {
            this.scrollToBottom()
            this.markRead()
          }
        }
        return
      }

      const th = this.threads.find(t => t.id === payload.thread_id)
      if (th) th.unread = (th.unread || 0) + 1
    },

    onRemoteUpdated (payload) {
      if (payload.thread_id !== this.activeThread) return
      const msg = payload.message || {}
      const existing = this.messages.find(m => m.id === msg.id)
      if (existing) {
        existing.body = msg.body
        existing.edited = true
      }
    },

    onRemoteDeleted (payload) {
      if (payload.thread_id !== this.activeThread) return
      const id = (payload.message || {}).id
      this.messages = this.messages.filter(m => m.id !== id)
    },

    onRemoteRead (payload) {
      if (payload.thread_id !== this.activeThread) return
      // Receipt counts are computed server-side and only exposed for our own
      // messages, so re-fetch rather than reconstructing them here.
      this.loadMessages(false)
    },

    /* ---------------------------------------------------------------- */
    /* Poll (fallback)                                                   */
    /* ---------------------------------------------------------------- */

    socketLive () {
      return !!(this.$projectChatRt && this.$projectChatRt.isConnected())
    },

    /**
     * The socket came up or went down (or a channel failed / recovered).
     * Re-pick the interval now, and when the socket is back, fetch once: a
     * reconnect may have missed messages, and after_id makes that cheap.
     */
    onTransportChange () {
      this.schedule()
      if (this.socketLive()) this.poll()
    },

    /**
     * Foreground again. poll() skipped every tick while hidden, so catch up
     * now instead of at the next one. The app-wide notifier nudges connect().
     */
    onVisibility () {
      if (document.hidden) return

      this.poll()
      this.schedule()
    },

    schedule () {
      const want = this.socketLive() ? SLOW_MS : FAST_MS
      if (this.timer && want === this.currentMs) return

      this.currentMs = want
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.poll()
        this.schedule()
      }, want)
    },

    poll () {
      if (document.hidden || !this.activeThread || !this.messages.length) return

      const afterId = this.messages[this.messages.length - 1].id
      hostAppApi.get(`${this.base}/threads/${this.activeThread}/messages?after_id=${afterId}`)
        .then(response => {
          const fresh = (response.data || {}).messages || []
          if (!fresh.length) return

          // Sample before appending — see the note in onRemoteCreated().
          const wasAtBottom = this.isViewingLatest()

          fresh.forEach(m => {
            if (!this.messages.some(x => x.id === m.id)) this.messages.push(m)
          })

          // Only auto-acknowledge when the reader is at the bottom.
          if (wasAtBottom) {
            this.scrollToBottom()
            this.markRead()
          }
        })
        .catch(() => { /* keep polling */ })
    },

    /* ---------------------------------------------------------------- */
    /* Presentation                                                      */
    /* ---------------------------------------------------------------- */

    showDay (index) {
      if (index === 0) return true
      return this.messages[index].day !== this.messages[index - 1].day
    },

    /**
     * Group consecutive messages from the same author, exactly as the web app
     * does: repeat the avatar and name only when the speaker changes, the day
     * breaks, or the unread separator interrupts — so a burst of three messages
     * reads as one turn instead of three headers.
     */
    isGrouped (index) {
      if (index === 0) return false
      const m = this.messages[index]
      const prev = this.messages[index - 1]

      return !!prev &&
        (prev.user || {}).id === (m.user || {}).id &&
        prev.day === m.day &&
        prev.mine === m.mine &&
        m.id !== this.firstUnreadId
    },

    dayLabel (day) {
      const d = moment(day, 'YYYY-MM-DD')
      if (d.isSame(moment(), 'day')) return this.$t('message.projects.chatToday')
      if (d.isSame(moment().subtract(1, 'day'), 'day')) return this.$t('message.projects.chatYesterday')
      return d.format('DD.MM.YYYY')
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.stream
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    /**
     * Is the reader actually looking at the newest messages?
     *
     * Gates the automatic markRead() on an incoming message. Without this the
     * PWA acknowledged every message the instant it arrived — even while
     * scrolled up in history, or with the app merely open in a background tab —
     * so the sender saw "Seen by 1" for messages nobody had read. The web app
     * has always applied this same check; the PWA was missing it.
     *
     * The 60px tolerance matches the web app: it absorbs sub-pixel rounding and
     * the last line's padding, so "close enough to the bottom" still counts as
     * looking at it.
     */
    isViewingLatest () {
      // Not visible at all — a hidden tab is definitively not being read.
      if (document.hidden) return false

      const el = this.$refs.stream
      if (!el) return false

      return el.scrollTop + el.clientHeight >= el.scrollHeight - 60
    }
  }
}
</script>

<style scoped>
/* Ported from the web app's project_chat.scss so the two clients read the
   same: one left column, grouped turns, no bubble fill, own messages marked by
   side + a "you" pill rather than colour. */

/* ---- Shell: sidebar + main, as the web app lays it out ----------------- */
.pcc-shell {
  display: flex;
  position: relative;
  height: calc(100vh - 140px);
  border: 1px solid rgba(127, 127, 127, .22);
  border-radius: 8px;
  overflow: hidden;
}

/* Inside the dialog the card owns the size, so fill it rather than measuring
   against the viewport, and drop the border/radius the v-card already draws. */
.pcc-shell-embedded {
  height: 100%;
  border: 0;
  border-radius: 0;
}

.pcc-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

/* ---- Sidebar ----------------------------------------------------------- */
.pcc-side {
  width: 220px;
  flex: none;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f7f9fc;
  border-right: 1px solid rgba(127, 127, 127, .22);
}

.pcc-side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px 6px;
}

.pcc-side-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #6b7385;
}

.pcc-new {
  border: 0;
  background: none;
  color: #0d6efd;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 4px;
}

.pcc-new:hover { background: rgba(13, 110, 253, .1); }

.pcc-count { font-size: 11px; color: #6b7385; }

/* Conversations get at most half the column so participants stay visible. */
.pcc-threadlist {
  padding: 0 6px 6px;
  overflow-y: auto;
  flex: none;
  max-height: 46%;
}

.pcc-side-section {
  border-top: 1px solid rgba(127, 127, 127, .22);
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.pcc-members {
  padding: 0 6px 6px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

/* ---- Thread rows ------------------------------------------------------- */
.pcc-thread {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
}

.pcc-thread:hover { background: rgba(127, 127, 127, .12); }
.pcc-thread-active { background: #e9f0ff; }
.pcc-thread-active .pcc-thread-name { color: #0d6efd; font-weight: 600; }

.pcc-thread-ic {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: #e3e7ef;
  color: #6b7385;
}

.pcc-thread-active .pcc-thread-ic { background: #0d6efd; color: #fff; }

.pcc-thread-txt {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

/* Both are spans; without display:block they run together on one line. */
.pcc-thread-name,
.pcc-thread-last {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pcc-thread-name { font-size: 13px; font-weight: 500; }
.pcc-thread-last { font-size: 11px; color: #9aa2b4; margin-top: 1px; }
.pcc-thread-none { opacity: .6; }

/* Overlapping stack for private threads. */
.pcc-facepile {
  display: inline-flex;
  flex: none;
}

.pcc-facepile .pcc-face { margin-left: -5px; }
.pcc-facepile .pcc-face:first-child { margin-left: 0; }

.pcc-initials-xs { font-size: 8px; font-weight: 700; }
.pcc-initials-sm { font-size: 9px; font-weight: 700; }

.pcc-badge {
  flex: none;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  background: #e11d48;
  color: #fff;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

/* ---- Participant rows -------------------------------------------------- */
.pcc-member {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 8px;
}

.pcc-member:hover { background: rgba(127, 127, 127, .12); }
.pcc-member-me .pcc-member-name { font-weight: 600; }

.pcc-member-name {
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.pcc-you {
  font-size: 10px;
  color: #6b7385;
  background: #e3e7ef;
  border-radius: 20px;
  padding: 0 6px;
  flex: none;
}

/* ---- Thread header ----------------------------------------------------- */
.pcc-thread-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid rgba(127, 127, 127, .22);
  flex: none;
}

.pcc-thread-head-txt { min-width: 0; flex: 1; }

.pcc-headtitle {
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pcc-headmeta {
  font-size: 11.5px;
  color: #9aa2b4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pcc-lockpill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #b45309;
  background: #fff5e6;
  border: 1px solid #b45309;
  border-radius: 20px;
  padding: 1px 8px;
}

/* The hamburger only exists while the sidebar is a drawer. */
.pcc-hamb {
  display: none;
  border: 1px solid rgba(127, 127, 127, .3);
  background: none;
  border-radius: 6px;
  padding: 3px 6px;
  cursor: pointer;
  flex: none;
}

.pcc-scrim {
  position: absolute;
  inset: 0;
  z-index: 24;
  background: rgba(10, 14, 22, .35);
}

.pcc-hint { font-size: 12px; color: #9aa2b4; }
.pcc-pick { margin-bottom: -6px; }

.pcc-stream {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 8px 14px;
}

.pcc-center {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.pcc-empty {
  font-size: 13px;
  color: #9aa2b4;
  text-align: center;
}

/* ---- Rows -------------------------------------------------------------- */
/* One shared left column. Full-bleed hover so a row is easy to track across
   the width, with the padding pulled back out by negative margin. */
.pcc-row {
  display: flex;
  gap: 10px;
  padding: 3px 8px;
  margin: 0 -8px;
  align-items: flex-start;
  border-radius: 6px;
}

.pcc-row:hover { background: rgba(127, 127, 127, .07); }

/* First message of a turn gets breathing room; grouped ones sit tight. */
.pcc-row:not(.pcc-grouped) { margin-top: 12px; }
.pcc-row:first-child { margin-top: 0; }
.pcc-row.pcc-grouped { margin-top: 0; }

/* Own messages sit on the right. No fill, no border, no tint — the side of the
   column plus the "you" pill is what marks them as yours. */
.pcc-row.pcc-me { flex-direction: row-reverse; }

.pcc-row.pcc-me .pcc-body {
  align-items: flex-end;
}

.pcc-row.pcc-me .pcc-text { text-align: right; }

/* Keep the natural name -> pill -> time order; only push the header to the
   right edge. Reversing it would render "13:17 you Simon". */
.pcc-row.pcc-me .pcc-head { justify-content: flex-end; }
.pcc-row.pcc-me .pcc-name { color: #0d6efd; }
.pcc-row.pcc-me .pcc-bubble-wrap { flex-direction: row-reverse; }
.pcc-row.pcc-me .pcc-foot { justify-content: flex-end; }

/* Reserved gutter so grouped rows keep their text aligned with the avatar of
   the message that started the turn. */
.pcc-av-slot {
  width: 26px;
  flex: none;
}

.pcc-initials {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .02em;
  text-transform: uppercase;
}

.pcc-body {
  min-width: 0;
  max-width: min(560px, 78%);
  display: flex;
  flex-direction: column;
}

/* ---- Header ------------------------------------------------------------ */
.pcc-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
}

.pcc-name {
  font-size: 12px;
  font-weight: 600;
  color: #4a5262;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pcc-you-tag {
  font-size: 10px;
  font-weight: 600;
  color: #6b7385;
  background: #eef1f6;
  border-radius: 20px;
  padding: 0 6px;
  flex: none;
}

.pcc-time {
  font-size: 10.5px;
  color: #a6adbb;
  flex: none;
  font-variant-numeric: tabular-nums;
}

/* Grouped rows have no header, so their time sits after the text and appears
   only on hover — a burst stays clean but is still checkable. */
.pcc-time-inline {
  opacity: 0;
  transition: opacity .12s ease;
}

.pcc-row:hover .pcc-time-inline { opacity: 1; }

/* ---- System messages from Weld IT (field-registered welds) ------------ */
/* Same treatment as the web chat: tinted card with a coloured edge, always
   left-aligned even when the author is the viewer. */
.pcc-row.pcc-sys .pcc-text {
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 3px solid #7c5cd6;
  background: #f3f0fd;
  text-align: left;
  color: #1f2330;
}
.pcc-row.pcc-sys-field_approved .pcc-text { border-left-color: #16a34a; background: #eef8f1; }
.pcc-row.pcc-sys-field_rejected .pcc-text { border-left-color: #e02f2f; background: #fdeeee; }
.pcc-row.pcc-me.pcc-sys .pcc-text { text-align: left; }
.pcc-sys-ic { margin-right: 5px; vertical-align: -2px; color: #7c5cd6; }
.pcc-row.pcc-sys-field_approved .pcc-sys-ic { color: #16a34a; }
.pcc-row.pcc-sys-field_rejected .pcc-sys-ic { color: #e02f2f; }
.pcc-sys-link {
  display: inline-flex; align-items: center; gap: 3px; margin-top: 8px;
  padding: 6px 10px; border-radius: 8px; border: 1px solid #bfdbfe; background: #fff;
  color: #0d6efd; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.theme--dark .pcc-row.pcc-sys .pcc-text { background: #2b2440; color: #e6eaf0; }
.theme--dark .pcc-row.pcc-sys-field_approved .pcc-text { background: #163a26; }
.theme--dark .pcc-row.pcc-sys-field_rejected .pcc-text { background: #3a1d1d; }
.theme--dark .pcc-sys-link { background: #16202e; border-color: #2a3a4e; }

/* ---- Text -------------------------------------------------------------- */
.pcc-bubble-wrap {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.pcc-text {
  padding: 2px 0;
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  min-width: 0;
}

.pcc-foot {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  font-size: 11px;
  color: #9aa2b4;
}

.pcc-edited { font-style: italic; font-size: 10.5px; }

.pcc-sent {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

/* ---- Message actions --------------------------------------------------- */
/* Hidden until the row is hovered so a quiet thread stays quiet. On touch
   there is no hover, so they are always visible below the pointer breakpoint
   (see the media query at the end). */
.pcc-acts {
  display: inline-flex;
  gap: 2px;
  flex: none;
  opacity: 0;
  transition: opacity .12s ease;
}

.pcc-row:hover .pcc-acts,
.pcc-acts:focus-within { opacity: 1; }

.pcc-act {
  border: 0;
  background: none;
  cursor: pointer;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 5px;
}

.pcc-act:hover { background: rgba(127, 127, 127, .15); }
.pcc-act-danger:hover { background: rgba(225, 29, 72, .12); }

/* ---- Separators -------------------------------------------------------- */
/* Centred pill on a hairline rule — clearer than a bare line with text. */
.pcc-day,
.pcc-unread {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 11px;
  margin: 18px 0 12px;
}

.pcc-day::before,
.pcc-unread::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
}

.pcc-day::before { background: rgba(127, 127, 127, .25); }
.pcc-unread::before { background: rgba(225, 29, 72, .35); }

.pcc-day span,
.pcc-unread span {
  position: relative;
  padding: 2px 12px;
  border-radius: 20px;
  font-weight: 600;
}

.pcc-day span {
  background: #eef1f6;
  color: #6b7385;
}

.pcc-unread span {
  background: #e11d48;
  color: #fff;
  font-weight: 700;
}

/* ---- Composer ---------------------------------------------------------- */
.pcc-compose {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid rgba(127, 127, 127, .22);
  flex: none;
}

.pcc-send { flex: none; }

.pcc-input { max-height: 120px; }

.pcc-edit-box {
  width: 100%;
  min-width: 220px;
}

/* The edit box is full width, so let it break out of the mirrored layout. */
.pcc-row.pcc-me .pcc-edit-box { text-align: left; }

.pcc-edit-actions {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  justify-content: flex-end;
}

/* ---- Dark theme -------------------------------------------------------- */
/* The PWA has a dark mode; the light-grey pills and names would vanish in it.
   5108: these MUST NOT use `::v-deep`. In a scoped block `::v-deep .theme--dark
   .x` compiles to `[data-v] .theme--dark .x`, which needs a scoped ancestor
   ABOVE the Vuetify root that carries `theme--dark` — there is none, so the
   rules never matched and the sidebar stayed light with white text on it.
   Plain `.theme--dark .x` compiles to `.theme--dark .x[data-v]`, which works. */
.theme--dark .pcc-name { color: #c7ccd6; }
.theme--dark .pcc-row.pcc-me .pcc-name { color: #6ea8fe; }
.theme--dark .pcc-you-tag { background: rgba(255, 255, 255, .12); color: #c7ccd6; }
.theme--dark .pcc-day span { background: #33373f; color: #c7ccd6; }
.theme--dark .pcc-row:hover { background: rgba(255, 255, 255, .05); }
.theme--dark .pcc-time { color: #7f8797; }
.theme--dark .pcc-foot,
.theme--dark .pcc-empty,
.theme--dark .pcc-headmeta,
.theme--dark .pcc-hint { color: #8a92a3; }

.theme--dark .pcc-side { background: #16202e; }
.theme--dark .pcc-side-label,
.theme--dark .pcc-count { color: #9aa2b4; }
.theme--dark .pcc-new { color: #6ea8fe; }
.theme--dark .pcc-thread-name,
.theme--dark .pcc-member-name { color: #e6eaf0; }
.theme--dark .pcc-thread-last { color: #8a92a3; }
.theme--dark .pcc-thread-ic { background: #2a3a4e; color: #c7ccd6; }
.theme--dark .pcc-thread-active { background: rgba(13, 110, 253, .22); }
.theme--dark .pcc-thread-active .pcc-thread-name { color: #6ea8fe; }
.theme--dark .pcc-thread-active .pcc-thread-ic { background: #0d6efd; color: #fff; }
.theme--dark .pcc-you { background: rgba(255, 255, 255, .12); color: #c7ccd6; }
.theme--dark .pcc-lockpill { background: rgba(180, 83, 9, .18); color: #f0b35c; border-color: #b45309; }
.theme--dark .pcc-hamb { color: #c7ccd6; }
.theme--dark .pcc-scrim { background: rgba(0, 0, 0, .55); }

/* ---- Sidebar as a drawer on narrow screens ----------------------------- */
/* Two columns do not fit on a phone, so the sidebar slides over the stream and
   the hamburger in the thread header appears to toggle it. */
@media (max-width: 820px) {
  .pcc-side {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 25;
    width: 240px;
    transform: translateX(-105%);
    transition: transform .2s ease;
    box-shadow: 0 6px 20px rgba(16, 24, 40, .18);
  }

  .pcc-side-open { transform: translateX(0); }
  .pcc-hamb { display: inline-flex; }
}

/* ---- Touch ------------------------------------------------------------- */
/* No hover on touch devices, so the row actions and the grouped timestamp
   would be permanently invisible. Reveal them instead. */
@media (hover: none) {
  .pcc-acts,
  .pcc-time-inline { opacity: 1; }
}

@media (max-width: 600px) {
  .pcc-body { max-width: 88%; }
}

@media (prefers-reduced-motion: reduce) {
  .pcc-acts,
  .pcc-time-inline { transition: none; }
  .pcc-side { transition: none; }
}
</style>
